const INCLUDED_ALLOCATION_REGEX = /^\s*Included\s+Allocation\s*/i;
const PARENTHESIS_CONTENT_REGEX = /^\(([^)]+)\)$/;
const PARENTHESIS_REGEX = /[()]/g;

export const toFriendlyMetricName = (rawName: string): string => {
  const mappings: Record<string, string> = {
    PTE: "Pro tool executions",
    STE: "Standard tool executions",
    UC: "User challenges",
  };
  if (!rawName) {
    return "";
  }
  let cleaned = rawName.replace(INCLUDED_ALLOCATION_REGEX, "").trim();
  const parenOnly = cleaned.match(PARENTHESIS_CONTENT_REGEX);
  if (parenOnly) {
    cleaned = parenOnly[1];
  }
  cleaned = cleaned.replace(PARENTHESIS_REGEX, "").trim();
  const upper = cleaned.toUpperCase();
  return mappings[upper] ?? cleaned;
};

type UsageEntry = { date: string } & Record<string, number>;
type MetricUsage = {
  billable_metric: { name: string };
  usage: Array<{ timeframe_end: string; quantity: number }>;
};

/**
 * Slugify a string to be used as a key in a record.
 */
export const slugify = (str: string): string =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

/**
 * Transforms cumulative usage data of multiple metrics into a unified array of entries,
 * where each entry represents usage quantities for all metrics on a given date.
 */
export const transformUsageData = (
  cumulativeUsageData: MetricUsage[],
  metricKeys: string[]
): UsageEntry[] => {
  if (cumulativeUsageData.length === 0) {
    return [];
  }

  const usageByDate = new Map<string, UsageEntry>();

  for (const [idx, metric] of cumulativeUsageData.entries()) {
    const key = metricKeys[idx];
    for (const sample of metric.usage) {
      const date = sample.timeframe_end.split("T")[0];
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const entry = usageByDate.get(date) ?? ({ date } as UsageEntry);
      entry[key] = sample.quantity ?? 0;
      usageByDate.set(date, entry);
    }
  }

  const entries = Array.from(usageByDate.values()).map((entry) => {
    for (const key of metricKeys) {
      if (entry[key] === undefined) {
        entry[key] = 0;
      }
    }
    return entry;
  });

  return entries.sort((a, b) => a.date.localeCompare(b.date));
};
